// Phase 10 — AdminDashboard.
//
// 10-widget founder/admin dashboard. Module Performance,
// Agent Accuracy, API Health, Signal Volume, Risk Flags,
// Performance Fee Dashboard, Churn Risk, Revenue by Module,
// Regulatory Risk Queue, Phantom Bet Performance.

import { useMemo } from "react";
import type { CSSProperties } from "react";
import { useDashboardWidgets } from "../../dashboard/useDashboardWidgets.ts";
import type { DashboardWidget } from "../../dashboard/useDashboardWidgets.ts";
import * as s from "./dashboard.styles.ts";

// ─── Widget definitions ───────────────────────────────────────

interface WidgetDef {
  key: string;
  label: string;
  icon: string;
  accent: "amber" | "green" | "red" | "blue";
  size: "small" | "medium" | "large";
}

const ADMIN_WIDGETS: WidgetDef[] = [
  { key: "module_performance", label: "Module Performance", icon: "◈", accent: "amber", size: "large" },
  { key: "agent_accuracy", label: "Agent Accuracy", icon: "◉", accent: "green", size: "medium" },
  { key: "api_health", label: "API Health", icon: "⟐", accent: "green", size: "medium" },
  { key: "signal_volume", label: "Signal Volume", icon: "◆", accent: "amber", size: "medium" },
  { key: "risk_flags", label: "Risk Flags", icon: "▲", accent: "red", size: "medium" },
  { key: "performance_fees", label: "Performance Fee Dashboard", icon: "◈", accent: "amber", size: "large" },
  { key: "churn_risk", label: "Churn Risk", icon: "▲", accent: "red", size: "medium" },
  { key: "revenue_by_module", label: "Revenue by Module", icon: "◈", accent: "amber", size: "large" },
  { key: "regulatory_queue", label: "Regulatory Risk Queue", icon: "⚙", accent: "red", size: "medium" },
  { key: "phantom_performance", label: "Phantom Bet Performance", icon: "◉", accent: "blue", size: "large" },
];

// ─── Helpers ──────────────────────────────────────────────────

function fmtValue(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v % 1 === 0 ? String(v) : v.toFixed(2);
}

function fmtPct(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${v.toFixed(1)}%`;
}

function getCardStyle(accent: string): CSSProperties {
  switch (accent) {
    case "green": return s.widgetCardAccent;
    case "red": return s.widgetCardWarning;
    case "blue": return s.widgetCardInfo;
    default: return s.widgetCard;
  }
}

function TrendIndicator({ widget }: { widget: DashboardWidget }): JSX.Element | null {
  if (!widget.trend) return null;
  const trendStyle =
    widget.trend === "up" ? s.trendUp
    : widget.trend === "down" ? s.trendDown
    : s.trendFlat;
  const arrow = widget.trend === "up" ? "↑" : widget.trend === "down" ? "↓" : "→";
  const pct = widget.trend_pct !== null ? ` ${fmtPct(widget.trend_pct)}` : "";

  return (
    <div style={s.widgetTrend}>
      <span style={trendStyle}>{arrow}{pct}</span>
    </div>
  );
}

// ─── Widget renderer ──────────────────────────────────────────

function WidgetCard({
  def,
  data,
}: {
  def: WidgetDef;
  data: DashboardWidget | undefined;
}): JSX.Element {
  const cardStyle = getCardStyle(def.accent);
  const displayValue = data
    ? (data.value_text ?? fmtValue(data.value_primary))
    : "—";
  const pctValue = data?.value_pct ?? null;

  const jsonItems = data?.value_json ?? {};
  const listEntries = Array.isArray(jsonItems.items)
    ? (jsonItems.items as { label: string; value: string }[])
    : [];

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={s.widgetLabel}>{def.label}</span>
        <span style={{ ...s.widgetLabel, fontSize: "14px", opacity: 0.4 }}>{def.icon}</span>
      </div>

      <span style={s.widgetValue}>{displayValue}</span>

      {listEntries.length > 0 ? (
        <div style={s.miniList}>
          {listEntries.map((item, i) => (
            <div key={i} style={s.miniRow}>
              <span style={s.miniLabel}>{item.label}</span>
              <span style={s.miniValue}>{item.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {pctValue !== null ? (
        <div style={s.progressTrack}>
          <div
            style={{
              ...(pctValue >= 80 ? s.progressFillGreen
                : pctValue <= 30 ? s.progressFillRed
                : s.progressFill),
              width: `${Math.min(pctValue, 100).toFixed(0)}%`,
            }}
          />
        </div>
      ) : null}

      {data ? <TrendIndicator widget={data} /> : null}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function AdminDashboard(): JSX.Element {
  const { widgets, loading } = useDashboardWidgets("admin");

  const widgetMap = useMemo(() => {
    const m = new Map<string, DashboardWidget>();
    for (const w of widgets) {
      m.set(w.widget_key, w);
    }
    return m;
  }, [widgets]);

  const smallWidgets = ADMIN_WIDGETS.filter((d) => d.size === "small" || d.size === "medium");
  const largeWidgets = ADMIN_WIDGETS.filter((d) => d.size === "large");

  return (
    <div style={s.dashRoot}>
      {/* Header */}
      <div style={s.dashHeader}>
        <div>
          <h2 style={s.dashTitle}>Admin Dashboard</h2>
          <div style={s.dashSubtitle}>
            Founder view · 10 widgets · system intelligence
          </div>
        </div>
        <div style={s.dashMeta}>
          {loading ? "Loading..." : `${String(widgetMap.size)} widgets active`}
        </div>
      </div>

      {/* KPI widgets (small/medium) */}
      <div style={s.widgetGrid}>
        {smallWidgets.map((def) => (
          <WidgetCard key={def.key} def={def} data={widgetMap.get(def.key)} />
        ))}
      </div>

      {/* Large widgets */}
      {largeWidgets.length > 0 ? (
        <div style={s.widgetGridLarge}>
          {largeWidgets.map((def) => (
            <WidgetCard key={def.key} def={def} data={widgetMap.get(def.key)} />
          ))}
        </div>
      ) : null}

      {/* Empty state when no admin data */}
      {widgets.length === 0 && !loading ? (
        <div style={s.emptyState}>
          <span style={s.emptyIcon}>⚙</span>
          <span style={s.emptyTitle}>Admin Metrics Initializing</span>
          <span style={s.emptyNote}>
            Module performance, agent accuracy, and revenue data will populate
            as the system processes signals and generates outcomes.
          </span>
        </div>
      ) : null}

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
